import { useContext, useId, useState } from "react";
import { ArrowDown, ArrowUp, Download, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import MerchantContext from "../../../contexts/MerchantContext";
import { trpc } from "../../../trpc";
import {
  BITRIX_IF_OPERATORS,
  BITRIX_STANDARD_FIELD_SUGGESTIONS,
  BITRIX_SUGGESTED_PATHS,
  emptyExprForMode,
  getExprMode,
  getKnownValuesForPath,
  normalizeUfCrmKey,
  stripUfCrmPrefix,
  type BitrixCustomFieldEntry,
  type BitrixExprMode,
  type BitrixFieldExpr,
  type BitrixIfBranch,
  type BitrixIfExpr,
  type BitrixIfOperator,
  type BitrixKnownValue,
  type BitrixLiteral,
  type BitrixLookupExpr,
  type BitrixLookupKey,
  type BitrixLookupRow,
  type BitrixSwitchCase,
  type BitrixSwitchExpr,
} from "./types";

// Custom UF_CRM_ fields force the prefix and offer enumeration autofill.
// "raw" fields are top-level Bitrix keys (CATEGORY_ID, TYPE_ID, STAGE_ID, …)
// that must be sent verbatim; no prefix, no enum autofill.
type BitrixFieldKeyMode = "ufCrm" | "raw";

const MODE_LABELS: Record<BitrixExprMode, string> = {
  literal: "Siempre lo mismo",
  path: "Copiar del prospecto",
  switch: "Traducir valores",
  if: "Según una condición",
  lookup: "Tabla de coincidencias",
};

const MODE_HELP: Record<BitrixExprMode, string> = {
  literal: "Manda siempre el mismo texto o número a Bitrix, sin importar el prospecto.",
  path: "Toma un dato del prospecto (nombre, teléfono, etapa, etc.) y lo manda tal cual.",
  switch: "Cuando un campo del prospecto llega con cierto valor, manda otro valor a Bitrix. Útil cuando Bitrix usa IDs en lugar de nombres.",
  if: "Si un campo cumple una condición, manda un valor; si no, manda otro.",
  lookup: "Busca dentro del texto de cada valor de Bitrix usando una o varias claves del prospecto. Si todas coinciden gana esa fila; si no, se relaja primero a la clave de mayor prioridad, luego a la siguiente.",
};

const InlineLabel = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): JSX.Element => (
  <Label className={cn("text-xs font-medium text-muted-foreground", className)}>
    {children}
  </Label>
);

const LiteralEditor = ({
  value,
  onChange,
}: {
  value: BitrixLiteral;
  onChange: (value: BitrixLiteral) => void;
}): JSX.Element => {
  const stringValue = value === null ? "" : String(value);
  return (
    <Input
      className="h-9 text-sm"
      value={stringValue}
      onChange={(e) => onChange(e.target.value)}
      placeholder="texto, número, o vacío para null"
    />
  );
};

const PathPicker = ({
  value,
  onChange,
  placeholder = "lead.profile.propertyCode",
  suggestedPaths,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  suggestedPaths?: ReadonlyArray<{ path: string; description: string }>;
}): JSX.Element => {
  const datalistId = `bitrix-paths-${useId()}`;
  const options = suggestedPaths ?? BITRIX_SUGGESTED_PATHS;
  return (
    <>
      <Input
        className="h-9 font-mono text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        list={datalistId}
      />
      <datalist id={datalistId}>
        {options.map((p) => (
          <option key={p.path} value={p.path}>
            {p.description}
          </option>
        ))}
      </datalist>
    </>
  );
};

const KnownValuesHint = ({
  values,
  onPick,
}: {
  values: readonly BitrixKnownValue[];
  onPick?: (value: string) => void;
}): JSX.Element => (
  <div className="mt-1.5 rounded-md border border-dashed border-border bg-muted/40 p-2">
    <div className="mb-1.5 flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
      <span className="font-medium text-foreground">
        Solo estos {values.length} valores pueden llegar aquí
      </span>
      {onPick ? <span>Toca uno para agregarlo abajo</span> : null}
    </div>
    <div
      className={cn(
        "flex max-h-32 flex-wrap gap-1 overflow-y-auto",
        // Fade the bottom edge when the list is long enough to overflow on
        // typical viewports — signals that there's more to scroll without
        // relying on a visible scrollbar (which mobile hides by default).
        values.length > 10 &&
          "[mask-image:linear-gradient(to_top,transparent_0,black_12px)]",
      )}
    >
      {values.map((v) => {
        const chipClass =
          "inline-flex items-center rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-foreground";
        const title = v.label && v.label !== v.value ? `${v.value} — ${v.label}` : v.value;
        return onPick ? (
          <button
            key={v.value}
            type="button"
            onClick={() => onPick(v.value)}
            title={title}
            className={cn(chipClass, "transition-colors hover:border-primary hover:bg-primary/10")}
          >
            {v.value}
          </button>
        ) : (
          <span key={v.value} title={title} className={chipClass}>
            {v.value}
          </span>
        );
      })}
    </div>
  </div>
);

const SwitchMatchInput = ({
  sourcePath,
  value,
  onChange,
}: {
  sourcePath: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element => {
  const datalistId = `bitrix-match-${useId()}`;
  const knownValues = getKnownValuesForPath(sourcePath);

  return (
    <>
      <Input
        className="h-11 flex-1 text-xs md:h-8"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={knownValues ? `ej. ${knownValues[0]?.value ?? ""}` : "ej. redirected"}
        list={knownValues ? datalistId : undefined}
      />
      {knownValues && (
        <datalist id={datalistId}>
          {knownValues.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label ?? v.value}
            </option>
          ))}
        </datalist>
      )}
    </>
  );
};

const SwitchEditor = ({
  value,
  onChange,
  onAutofillRequest,
}: {
  value: BitrixSwitchExpr;
  onChange: (value: BitrixSwitchExpr) => void;
  onAutofillRequest?: () => Promise<BitrixSwitchCase[] | null>;
}): JSX.Element => {
  const [isAutofilling, setIsAutofilling] = useState(false);

  const handleAutofill = async () => {
    if (!onAutofillRequest) return;
    setIsAutofilling(true);
    try {
      const fetched = await onAutofillRequest();
      if (fetched && fetched.length > 0) {
        // Merge rather than replace: preserve every existing case (match + output)
        // and append only fetched cases whose match isn't already present. This
        // means a user who already configured 5 mappings doesn't lose them.
        const existingMatches = new Set(value.cases.map((c) => c.match));
        const newCases = fetched.filter((c) => !existingMatches.has(c.match));
        if (newCases.length === 0) {
          toast.info("Todos los valores ya están en la tabla");
          return;
        }
        onChange({ ...value, cases: [...value.cases, ...newCases] });
      }
    } finally {
      setIsAutofilling(false);
    }
  };
  const addCase = () => {
    const newCase: BitrixSwitchCase = { match: "", output: "" };
    onChange({ ...value, cases: [newCase, ...value.cases] });
  };

  const updateCase = (idx: number, patch: Partial<BitrixSwitchCase>) => {
    onChange({
      ...value,
      cases: value.cases.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    });
  };

  const removeCase = (idx: number) => {
    onChange({ ...value, cases: value.cases.filter((_, i) => i !== idx) });
  };

  return (
    <div className="flex flex-col gap-3 rounded-md bg-muted/40 p-3">
      <div>
        <InlineLabel>Qué campo traducir</InlineLabel>
        <div className="mt-1">
          <PathPicker
            value={value.source.path}
            onChange={(path) => onChange({ ...value, source: { ...value.source, path } })}
            placeholder="lead.pipelinePositionId"
          />
        </div>
        {(() => {
          const known = getKnownValuesForPath(value.source.path);
          if (!known) return null;
          const handlePick = (match: string) => {
            if (value.cases.some((c) => c.match === match)) return;
            onChange({ ...value, cases: [...value.cases, { match, output: "" }] });
          };
          return <KnownValuesHint values={known} onPick={handlePick} />;
        })()}
      </div>

      <div>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <InlineLabel>Valor del prospecto → valor en Bitrix</InlineLabel>
          <div className="flex items-center gap-1">
            {onAutofillRequest && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAutofill}
                disabled={isAutofilling}
                className="h-11 gap-1 px-3 text-xs md:h-7 md:px-2"
                title="Consulta el esquema de Bitrix y llena los casos con VALUE → ID"
              >
                {isAutofilling ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Download className="size-3" />
                )}
                Autocompletar
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={addCase}
              className="h-11 gap-1 px-3 text-xs text-muted-foreground hover:text-foreground md:h-7 md:px-2"
            >
              <Plus className="size-3" /> Agregar
            </Button>
          </div>
        </div>
        {value.cases.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Aún no hay traducciones. Agrega una para decir: cuando el prospecto llega con{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              este valor
            </code>
            , manda{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              este otro
            </code>{" "}
            a Bitrix.
          </p>
        ) : (
          <div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
            {value.cases.length > 6 && (
              <p className="sticky top-0 z-10 mb-1 border-b border-border bg-card px-2 py-1 text-xs text-muted-foreground">
                Mostrando {value.cases.length} equivalencias — desplázate para ver todas
              </p>
            )}
            {value.cases.map((c, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <SwitchMatchInput
                  sourcePath={value.source.path}
                  value={c.match}
                  onChange={(match) => updateCase(idx, { match })}
                />
                <span className="shrink-0 text-xs text-muted-foreground">→</span>
                <Input
                  className="h-11 flex-1 font-mono text-xs md:h-8"
                  value={c.output === null ? "" : String(c.output)}
                  onChange={(e) => updateCase(idx, { output: e.target.value })}
                  placeholder="ej. 48428"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-11 shrink-0 text-muted-foreground hover:text-destructive md:size-7"
                  onClick={() => removeCase(idx)}
                  aria-label="Eliminar equivalencia"
                >
                  <Trash2 className="size-3.5 md:size-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <InlineLabel>Si no coincide con nada de arriba, mandar</InlineLabel>
        <div className="mt-1">
          <Input
            className="h-9 text-sm"
            value={value.default === null ? "" : String(value.default)}
            onChange={(e) =>
              onChange({ ...value, default: e.target.value === "" ? null : e.target.value })
            }
            placeholder="Vacío = null"
          />
        </div>
      </div>
    </div>
  );
};

const BranchEditor = ({
  label,
  branch,
  onChange,
  literalPlaceholder,
}: {
  label: string;
  branch: BitrixIfBranch;
  onChange: (branch: BitrixIfBranch) => void;
  literalPlaceholder?: string;
}): JSX.Element => {
  const kind = branch.kind;

  const handleKindChange = (nextKind: "literal" | "path") => {
    if (nextKind === kind) return;
    if (nextKind === "literal") onChange({ kind: "literal", value: "" });
    else onChange({ kind: "path", path: "" });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <InlineLabel>{label}</InlineLabel>
      <div className="flex items-start gap-2">
        <Select value={kind} onValueChange={(v) => handleKindChange(v as "literal" | "path")}>
          <SelectTrigger className="h-9 w-36 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="literal" className="text-xs">
              Valor fijo
            </SelectItem>
            <SelectItem value="path" className="text-xs">
              Campo del prospecto
            </SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1">
          {kind === "literal" ? (
            <Input
              className="h-9 text-sm"
              value={branch.value === null ? "" : String(branch.value)}
              onChange={(e) =>
                onChange({
                  kind: "literal",
                  value: e.target.value,
                })
              }
              placeholder={literalPlaceholder ?? "texto, número, o vacío para null"}
            />
          ) : (
            <PathPicker
              value={branch.path}
              onChange={(path) => onChange({ kind: "path", path })}
              placeholder="lead.profile.propertyCode"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const IfEditor = ({
  value,
  onChange,
}: {
  value: BitrixIfExpr;
  onChange: (value: BitrixIfExpr) => void;
}): JSX.Element => {
  const operatorMeta =
    BITRIX_IF_OPERATORS.find((op) => op.id === value.condition.operator) ??
    BITRIX_IF_OPERATORS[0];

  const handleOperatorChange = (operator: BitrixIfOperator) => {
    const meta = BITRIX_IF_OPERATORS.find((op) => op.id === operator);
    onChange({
      ...value,
      condition: {
        ...value.condition,
        operator,
        compareValue: meta?.requiresCompareValue ? value.condition.compareValue ?? "" : undefined,
      },
    });
  };

  return (
    <div className="flex flex-col gap-3 rounded-md bg-muted/40 p-3">
      <div>
        <InlineLabel>Si el campo</InlineLabel>
        <div className="mt-1">
          <PathPicker
            value={value.condition.path}
            onChange={(path) =>
              onChange({ ...value, condition: { ...value.condition, path } })
            }
            placeholder="lead.profile.propertyCode"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <InlineLabel>Comparación</InlineLabel>
          <div className="mt-1">
            <Select
              value={value.condition.operator}
              onValueChange={(v) => handleOperatorChange(v as BitrixIfOperator)}
            >
              <SelectTrigger className="h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BITRIX_IF_OPERATORS.map((op) => (
                  <SelectItem key={op.id} value={op.id} className="text-xs">
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {operatorMeta.requiresCompareValue && (
          <div>
            <InlineLabel>Valor a comparar</InlineLabel>
            <div className="mt-1">
              <Input
                className="h-9 text-sm"
                value={value.condition.compareValue ?? ""}
                onChange={(e) =>
                  onChange({
                    ...value,
                    condition: { ...value.condition, compareValue: e.target.value },
                  })
                }
                placeholder="ej. redirected, 0, 100"
              />
            </div>
          </div>
        )}
      </div>

      <BranchEditor
        label="Entonces devuelve"
        branch={value.then}
        onChange={(then) => onChange({ ...value, then })}
        literalPlaceholder="ej. https://...-{{lead.profile.propertyCode}}"
      />
      <BranchEditor
        label="Si no, devuelve"
        branch={value.else}
        onChange={(elseBranch) => onChange({ ...value, else: elseBranch })}
        literalPlaceholder="Vacío = no se envía el campo"
      />
    </div>
  );
};

const LookupEditor = ({
  value,
  onChange,
  onAutofillRequest,
}: {
  value: BitrixLookupExpr;
  onChange: (value: BitrixLookupExpr) => void;
  // Reuses the existing switch autofill — Bitrix enumeration items map 1:1 to
  // lookup rows (VALUE → match, ID → output).
  onAutofillRequest?: () => Promise<BitrixSwitchCase[] | null>;
}): JSX.Element => {
  const [isAutofilling, setIsAutofilling] = useState(false);

  const updateKey = (idx: number, patch: Partial<BitrixLookupKey>) =>
    onChange({
      ...value,
      keys: value.keys.map((k, i) => (i === idx ? { ...k, ...patch } : k)),
    });

  const moveKey = (idx: number, delta: -1 | 1) => {
    const target = idx + delta;
    if (target < 0 || target >= value.keys.length) return;
    const next = [...value.keys];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange({ ...value, keys: next });
  };

  const removeKey = (idx: number) => {
    if (value.keys.length === 1) return;
    onChange({ ...value, keys: value.keys.filter((_, i) => i !== idx) });
  };

  const addKey = () => onChange({ ...value, keys: [...value.keys, { path: "" }] });

  const updateRow = (idx: number, patch: Partial<BitrixLookupRow>) =>
    onChange({
      ...value,
      rows: value.rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    });

  const moveRow = (idx: number, delta: -1 | 1) => {
    const target = idx + delta;
    if (target < 0 || target >= value.rows.length) return;
    const next = [...value.rows];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange({ ...value, rows: next });
  };

  const removeRow = (idx: number) =>
    onChange({ ...value, rows: value.rows.filter((_, i) => i !== idx) });

  const addRow = () =>
    onChange({ ...value, rows: [{ match: "", output: "" }, ...value.rows] });

  const handleAutofill = async () => {
    if (!onAutofillRequest) return;
    setIsAutofilling(true);
    try {
      const fetched = await onAutofillRequest();
      if (fetched && fetched.length > 0) {
        const existing = new Set(value.rows.map((r) => r.match));
        const additions = fetched
          .filter((c) => !existing.has(c.match))
          .map((c) => ({ match: c.match, output: c.output }));
        if (additions.length === 0) {
          toast.info("Todos los valores ya están en la tabla");
          return;
        }
        onChange({ ...value, rows: [...value.rows, ...additions] });
      }
    } finally {
      setIsAutofilling(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-md bg-muted/40 p-3">
      <div>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <InlineLabel>Claves a buscar (orden = prioridad)</InlineLabel>
          <Button
            variant="ghost"
            size="sm"
            onClick={addKey}
            className="h-11 gap-1 px-3 text-xs text-muted-foreground hover:text-foreground md:h-7 md:px-2"
          >
            <Plus className="size-3" /> Agregar clave
          </Button>
        </div>
        <div className="flex flex-col gap-1.5">
          {value.keys.map((k, idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              <div className="flex flex-1 flex-col gap-1 sm:flex-row">
                <div className="flex-1">
                  <PathPicker
                    value={k.path}
                    onChange={(path) => updateKey(idx, { path })}
                    placeholder="listingRequest.neighborhood"
                  />
                </div>
                <Input
                  className="h-9 text-sm sm:w-44"
                  value={k.label ?? ""}
                  onChange={(e) => updateKey(idx, { label: e.target.value })}
                  placeholder="Etiqueta (opcional)"
                />
              </div>
              <div className="flex shrink-0 items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-11 text-muted-foreground hover:text-foreground md:size-7"
                  onClick={() => moveKey(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Subir clave"
                >
                  <ArrowUp className="size-3.5 md:size-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-11 text-muted-foreground hover:text-foreground md:size-7"
                  onClick={() => moveKey(idx, 1)}
                  disabled={idx === value.keys.length - 1}
                  aria-label="Bajar clave"
                >
                  <ArrowDown className="size-3.5 md:size-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-11 text-muted-foreground hover:text-destructive md:size-7"
                  onClick={() => removeKey(idx)}
                  disabled={value.keys.length === 1}
                  aria-label="Eliminar clave"
                >
                  <Trash2 className="size-3.5 md:size-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <InlineLabel>Tabla de valores (Bitrix VALUE → ID)</InlineLabel>
          <div className="flex items-center gap-1">
            {onAutofillRequest && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAutofill}
                disabled={isAutofilling}
                className="h-11 gap-1 px-3 text-xs md:h-7 md:px-2"
                title="Consulta el esquema de Bitrix y agrega los valores"
              >
                {isAutofilling ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Download className="size-3" />
                )}
                Autocompletar
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={addRow}
              className="h-11 gap-1 px-3 text-xs text-muted-foreground hover:text-foreground md:h-7 md:px-2"
            >
              <Plus className="size-3" /> Agregar fila
            </Button>
          </div>
        </div>
        {value.rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Aún no hay filas. Agrega una manualmente o usa Autocompletar para traer las
            opciones del campo desde Bitrix.
          </p>
        ) : (
          <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
            {value.rows.length > 6 && (
              <p className="sticky top-0 z-10 mb-1 border-b border-border bg-card px-2 py-1 text-xs text-muted-foreground">
                {value.rows.length} filas — las más específicas deben ir arriba.
              </p>
            )}
            {value.rows.map((r, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <Input
                  className="h-11 flex-1 text-xs md:h-8"
                  value={r.match}
                  onChange={(e) => updateRow(idx, { match: e.target.value })}
                  placeholder="ej. Bogotá - Chapinero"
                />
                <span className="shrink-0 text-xs text-muted-foreground">→</span>
                <Input
                  className="h-11 flex-1 font-mono text-xs md:h-8"
                  value={r.output === null ? "" : String(r.output)}
                  onChange={(e) => updateRow(idx, { output: e.target.value })}
                  placeholder="ej. 48428"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-11 shrink-0 text-muted-foreground hover:text-foreground md:size-7"
                  onClick={() => moveRow(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Subir fila"
                >
                  <ArrowUp className="size-3.5 md:size-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-11 shrink-0 text-muted-foreground hover:text-foreground md:size-7"
                  onClick={() => moveRow(idx, 1)}
                  disabled={idx === value.rows.length - 1}
                  aria-label="Bajar fila"
                >
                  <ArrowDown className="size-3.5 md:size-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-11 shrink-0 text-muted-foreground hover:text-destructive md:size-7"
                  onClick={() => removeRow(idx)}
                  aria-label="Eliminar fila"
                >
                  <Trash2 className="size-3.5 md:size-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <p className="mt-2 text-[11px] text-muted-foreground">
          Las claves se buscan dentro del texto de cada VALUE (sin acentos, minúsculas,
          sin espacios). Si todas coinciden gana esa fila; si no, se relaja a la clave de
          mayor prioridad, y luego a la siguiente.
        </p>
      </div>

      <div>
        <InlineLabel>Si nada coincide, mandar</InlineLabel>
        <div className="mt-1">
          <Input
            className="h-9 text-sm"
            value={value.default === null ? "" : String(value.default)}
            onChange={(e) =>
              onChange({ ...value, default: e.target.value === "" ? null : e.target.value })
            }
            placeholder="Vacío = null"
          />
        </div>
      </div>
    </div>
  );
};

export const BitrixExprValueEditor = ({
  value,
  onChange,
  onSwitchAutofillRequest,
  pathSuggestions,
  pathModeLabel,
}: {
  value: BitrixFieldExpr;
  onChange: (value: BitrixFieldExpr) => void;
  onSwitchAutofillRequest?: () => Promise<BitrixSwitchCase[] | null>;
  // Overrides the global path-suggestion list for the top-level `path` mode
  // only. Switch/If submodes keep the global list since their inner paths can
  // legitimately point anywhere.
  pathSuggestions?: ReadonlyArray<{ path: string; description: string }>;
  // Overrides the "Copiar del prospecto" label for the path mode when the
  // editor is bound to a specific field whose source is obvious (e.g. the
  // contact phone, where the path is always lead.phone).
  pathModeLabel?: string;
}): JSX.Element => {
  const mode = getExprMode(value);
  const modeLabel = (m: BitrixExprMode): string =>
    m === "path" && pathModeLabel ? pathModeLabel : MODE_LABELS[m];

  const handleModeChange = (nextMode: BitrixExprMode) => {
    if (nextMode === mode) return;
    onChange(emptyExprForMode(nextMode));
  };

  return (
    <div className="flex flex-col gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Select value={mode} onValueChange={(v) => handleModeChange(v as BitrixExprMode)}>
                <SelectTrigger className="h-11 w-full text-xs md:h-8 sm:w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["literal", "path", "switch", "lookup", "if"] as const).map((m) => (
                    <SelectItem key={m} value={m} className="text-xs">
                      {modeLabel(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs">{MODE_HELP[mode]}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {mode === "literal" && (
        <LiteralEditor value={value as BitrixLiteral} onChange={onChange} />
      )}

      {mode === "path" && (
        <PathPicker
          value={(value as { path: string }).path}
          onChange={(path) => onChange({ mode: "path", path })}
          suggestedPaths={pathSuggestions}
        />
      )}

      {mode === "switch" && (
        <SwitchEditor
          value={value as BitrixSwitchExpr}
          onChange={onChange}
          onAutofillRequest={onSwitchAutofillRequest}
        />
      )}

      {mode === "lookup" && (
        <LookupEditor
          value={value as BitrixLookupExpr}
          onChange={onChange}
          onAutofillRequest={onSwitchAutofillRequest}
        />
      )}

      {mode === "if" && <IfEditor value={value as BitrixIfExpr} onChange={onChange} />}
    </div>
  );
};

// Which Bitrix entity the editor validates UF_CRM_* keys against
// (crm.<entity>.fields). Contacts have their own user-field namespace.
type BitrixFieldsEntity = "lead" | "deal" | "contact";

// `rowLabel` sits on every row so a field deep in a long list still says which
// entity it belongs to once the section header has scrolled away.
const BITRIX_FIELDS_ENTITY: Record<BitrixFieldsEntity, { rowLabel: string; plural: string }> = {
  lead: { rowLabel: "Campo del lead", plural: "los leads" },
  deal: { rowLabel: "Campo del deal", plural: "los deals" },
  contact: { rowLabel: "Campo del contacto", plural: "los contactos" },
};

const ExprRow = ({
  entry,
  onChange,
  onRemove,
  keyMode,
  entity,
}: {
  entry: BitrixCustomFieldEntry;
  onChange: (entry: BitrixCustomFieldEntry) => void;
  onRemove: () => void;
  keyMode: BitrixFieldKeyMode;
  entity: BitrixFieldsEntity;
}): JSX.Element => {
  const isRawKey = keyMode === "raw";
  const displaySuffix = isRawKey ? entry.key : stripUfCrmPrefix(entry.key);

  const { merchantId } = useContext(MerchantContext);
  const utils = trpc.useUtils();

  const handleSwitchAutofill = async (): Promise<BitrixSwitchCase[] | null> => {
    if (isRawKey) {
      // Raw top-level fields (CATEGORY_ID, …) are not enumerations on Bitrix's
      // user-fields API, so we have nothing to autofill.
      toast.error("Autocompletado solo aplica a campos UF_CRM_");
      return null;
    }
    if (!merchantId) {
      toast.error("No hay merchant seleccionado");
      return null;
    }
    const fullKey = normalizeUfCrmKey(entry.key);
    if (!fullKey) {
      toast.error("Configura el campo UF_CRM_ primero", {
        description: "Ingresa el número del campo para poder consultar Bitrix.",
      });
      return null;
    }
    try {
      const res = await utils.crmSelfConfig.getBitrixEntityFields.fetch({
        merchantId,
        entity,
      });
      const fieldDef = res.fields?.[fullKey];

      if (!fieldDef || typeof fieldDef !== "object") {
        toast.error(`Bitrix no conoce el campo ${fullKey} en ${entity}`, {
          description: `Verifica el número o que el campo exista en ${BITRIX_FIELDS_ENTITY[entity].plural} de esta cuenta Bitrix.`,
        });
        return null;
      }

      const def = fieldDef as { type?: string; items?: Array<{ ID?: unknown; VALUE?: unknown }> };
      if (def.type !== "enumeration" || !Array.isArray(def.items) || def.items.length === 0) {
        toast.error(`${fullKey} no es una lista de opciones`, {
          description: `Tipo reportado por Bitrix: ${def.type ?? "desconocido"}. El autocompletado solo aplica a campos tipo "enumeration".`,
        });
        return null;
      }

      const cases: BitrixSwitchCase[] = def.items
        .map((item) => {
          const match = item.VALUE == null ? "" : String(item.VALUE);
          const rawId = item.ID;
          const numericId = rawId == null ? NaN : Number(rawId);
          const output = Number.isFinite(numericId) ? numericId : (rawId == null ? "" : String(rawId));
          return { match, output };
        })
        .filter((c) => c.match !== "");

      if (cases.length === 0) {
        toast.error(`${fullKey} no devolvió opciones válidas`);
        return null;
      }

      toast.success(`${cases.length} opciones cargadas desde Bitrix`);
      return cases;
    } catch (error: any) {
      toast.error("No se pudieron cargar las opciones", {
        description: error?.message || String(error),
      });
      return null;
    }
  };

  return (
    <div className="relative flex flex-col gap-3 rounded-md border border-border bg-card p-3">
      <button
        type="button"
        onClick={onRemove}
        aria-label="Eliminar campo"
        className="absolute right-2 top-2 flex size-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive md:size-7"
      >
        <Trash2 className="size-3.5" />
      </button>

      <div className="grid grid-cols-1 gap-3 pr-8 md:grid-cols-2">
        <div>
          <InlineLabel>{BITRIX_FIELDS_ENTITY[entity].rowLabel}</InlineLabel>
          {isRawKey ? (
            <div className="mt-1 flex flex-col gap-1.5">
              <Input
                className="h-9 font-mono text-sm"
                value={displaySuffix}
                onChange={(e) => onChange({ ...entry, key: e.target.value })}
                placeholder="CATEGORY_ID, MI_CAMPO_PROPIO, …"
              />
              <div className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                <span className="mr-0.5">Ejemplos:</span>
                {BITRIX_STANDARD_FIELD_SUGGESTIONS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    title={s.description}
                    onClick={() => onChange({ ...entry, key: s.key })}
                    className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono transition-colors hover:bg-muted"
                  >
                    {s.key}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-1 flex">
              <span className="inline-flex h-9 items-center rounded-l-md border border-r-0 border-input bg-muted px-2.5 font-mono text-xs text-muted-foreground">
                UF_CRM_
              </span>
              <Input
                className="h-9 rounded-l-none font-mono text-sm"
                value={displaySuffix}
                onChange={(e) => onChange({ ...entry, key: stripUfCrmPrefix(e.target.value) })}
                placeholder="1727189949"
              />
            </div>
          )}
        </div>

        <div>
          <InlineLabel>Etiqueta interna (opcional)</InlineLabel>
          <div className="mt-1">
            <Input
              className="h-9 text-sm"
              value={entry.label ?? ""}
              onChange={(e) => onChange({ ...entry, label: e.target.value })}
              placeholder="Ej. Etapa del embudo, Ciudad objetivo..."
            />
          </div>
        </div>
      </div>

      <div>
        <InlineLabel>Origen del valor</InlineLabel>
        <div className="mt-1.5">
          <BitrixExprValueEditor
            value={entry.value}
            onChange={(value) => onChange({ ...entry, value })}
            onSwitchAutofillRequest={handleSwitchAutofill}
          />
        </div>
      </div>
    </div>
  );
};

export const BitrixCustomFieldsEditor = ({
  entries,
  onChange,
  emptyLabel,
  addLabel = "Agregar campo",
  keyMode = "ufCrm",
  entity,
}: {
  entries: BitrixCustomFieldEntry[];
  onChange: (entries: BitrixCustomFieldEntry[]) => void;
  emptyLabel: string;
  // Names the target entity on the button ("Agregar campo al contacto") so the
  // action itself says where the field goes, even far below the section header.
  addLabel?: string;
  keyMode?: BitrixFieldKeyMode;
  entity: BitrixFieldsEntity;
}): JSX.Element => {
  const handleChangeRow = (idx: number, entry: BitrixCustomFieldEntry) => {
    onChange(entries.map((e, i) => (i === idx ? entry : e)));
  };
  const handleRemoveRow = (idx: number) => {
    onChange(entries.filter((_, i) => i !== idx));
  };
  const handleAdd = () => {
    onChange([{ key: "", value: "" }, ...entries]);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          {entries.length === 0
            ? emptyLabel
            : `${entries.length} campo${entries.length === 1 ? "" : "s"} configurado${entries.length === 1 ? "" : "s"}`}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAdd}
          className="h-11 gap-1.5 px-3 text-xs md:h-8 md:px-2.5"
        >
          <Plus className="size-3.5" /> {addLabel}
        </Button>
      </div>
      {entries.length > 0 && (
        <div className="flex flex-col gap-2">
          {entries.map((entry, idx) => (
            <ExprRow
              key={idx}
              entry={entry}
              onChange={(next) => handleChangeRow(idx, next)}
              onRemove={() => handleRemoveRow(idx)}
              keyMode={keyMode}
              entity={entity}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { getExprMode };

export type { BitrixCustomFieldEntry } from "./types";
